import { useMemo } from "react";
import {
  AlertTriangle,
  BarChart3,
  LineChart,
  ShieldCheck,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import {
  buildBlockHeroCopy,
  buildBlockHeroStats,
  buildBlockPerformanceActions,
  buildBlockPerformanceInsights,
  buildBlockRankingTable,
  buildCategoryMix,
  buildErrorHeatline,
  buildLoadRanking,
  fmt,
  formatLakhShort,
  pct,
} from "@/utils/dropboxDerived";
import HeroStatusChip from "@/components/HeroStatusChip";
import "./DropboxBlockPerformanceTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const INSIGHT_ICONS = {
  "": BarChart3,
  red: AlertTriangle,
  green: ShieldCheck,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  "": BarChart3,
  green: ShieldCheck,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const titleCase = (name = "") =>
  name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const DropboxBlockPerformanceTab = ({ overview = {}, blockData = [] }) => {
  const heroStats = useMemo(() => buildBlockHeroStats(blockData, overview), [blockData, overview]);
  const heroCopy = useMemo(() => buildBlockHeroCopy(blockData), [blockData]);
  const insights = useMemo(() => buildBlockPerformanceInsights(blockData), [blockData]);
  const loadRanking = useMemo(() => buildLoadRanking(blockData), [blockData]);
  const errorHeatline = useMemo(() => buildErrorHeatline(blockData), [blockData]);
  const rankingTable = useMemo(() => buildBlockRankingTable(blockData), [blockData]);
  const categoryMix = useMemo(() => buildCategoryMix(blockData), [blockData]);
  const actions = useMemo(() => buildBlockPerformanceActions(blockData), [blockData]);

  const tableRemarksTotal = rankingTable.reduce((sum, row) => sum + (row.total_remarks ?? 0), 0);

  return (
    <div className="dbx-blocks-tab" data-testid="dropbox-block-performance">
      <section className="dbx-hero-card" id="dbx-block-hero">
        <div className="dbx-hero-layout dbx-block-hero-layout">
          <div className="dbx-block-hero-copy">
            <h2>Block Performance Command Center</h2>
            <p>{heroCopy}</p>
            <div className="dbx-status-row">
              <HeroStatusChip chipClass="dbx-status-chip" showChevron={false}>
                {heroStats.blockCount} Blocks in Scope
              </HeroStatusChip>
              <HeroStatusChip
                chipClass="dbx-status-chip"
                variant="amber"
                onClick={heroStats.highErrorCount > 0 ? () => scrollToId("dbx-ranking") : undefined}
                ariaLabel={
                  heroStats.highErrorCount > 0
                    ? `${heroStats.highErrorCount} high-error blocks — view block ranking`
                    : undefined
                }
              >
                {heroStats.highErrorCount} High-Error Blocks
              </HeroStatusChip>
              <HeroStatusChip
                chipClass="dbx-status-chip"
                variant="red"
                onClick={heroStats.totalDropout > 0 ? () => scrollToId("dbx-block-actions") : undefined}
                ariaLabel={
                  heroStats.totalDropout > 0
                    ? `${fmt(heroStats.totalDropout)} dropout records — view action queue`
                    : undefined
                }
              >
                {fmt(heroStats.totalDropout)} Dropout Records
              </HeroStatusChip>
              <HeroStatusChip
                chipClass="dbx-status-chip"
                onClick={() => scrollToId("dbx-diagnostics")}
                ariaLabel={`${formatLakhShort(heroStats.totalRemarks)} remarks reviewed — view diagnostics`}
              >
                {formatLakhShort(heroStats.totalRemarks)} Remarks Reviewed
              </HeroStatusChip>
            </div>
            <div className="dbx-range-card">
              <div className="dbx-range-head">
                <b>Remark intensity spread</b>
                <span>Avg remarks/school range</span>
              </div>
              <div className="dbx-range-track">
                <i />
              </div>
              <div className="dbx-range-scale">
                <span>
                  Low: {titleCase(heroStats.rangeLow?.block_name)} {heroStats.rangeLow?.avg_remarks_per_school}
                </span>
                <span>
                  High: {titleCase(heroStats.rangeHigh?.block_name)} {heroStats.rangeHigh?.avg_remarks_per_school}
                </span>
              </div>
            </div>
          </div>

          <div className="dbx-block-metric-grid" aria-label="Block performance KPIs">
            {heroStats.kpis.map((card) => (
              <article key={card.label} className="dbx-block-metric-card">
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span className={`dbx-metric-sub ${card.subClass}`}>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dbx-block-insight-grid" aria-label="Executive insights">
        {insights.map((item) => {
          const Icon = INSIGHT_ICONS[item.iconClass] ?? BarChart3;
          return (
            <article key={item.id} className="dbx-block-insight-card">
              <div className="dbx-insight-top">
                <div className={`dbx-icon-box ${item.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`dbx-pill ${item.pillClass}`.trim()}>{item.pill}</span>
              </div>
              <h3>{toTitleCase(item.title)}</h3>
              <p>{item.body}</p>
            </article>
          );
        })}
      </section>

      <div className="dbx-section-title" id="dbx-diagnostics">
        <div>
          <h2>Performance Diagnostics</h2>
          <p>
            Charts are split into readable panels so officers can quickly understand volume, error
            pressure and dropout priority without scanning a dense table first.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-ranking")}>
          View full ranking →
        </button>
      </div>

      <section className="dbx-blocks-analytics-grid">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Remark Load Ranking</h3>
              <p>
                Top blocks by total remarks. The bars highlight where operational review time should
                be allocated first.
              </p>
            </div>
            <span className="dbx-pill">Total remarks</span>
          </div>
          <div className="dbx-rank-list" aria-label="Block remark load ranking">
            {loadRanking.map((row) => (
              <div key={row.block_name} className="dbx-rank-row">
                <div className="dbx-rank-label">{row.label}</div>
                <div className="dbx-rank-track">
                  <div className="dbx-rank-bar" style={{ width: `${row.widthPct}%` }} />
                </div>
                <div className="dbx-rank-value">{row.displayValue}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Error Risk Heatline</h3>
              <p>
                Risk marker position represents error percentage. Red points require immediate
                validation and field-level correction.
              </p>
            </div>
            <span className="dbx-pill red">Error %</span>
          </div>
          <div className="dbx-risk-matrix" aria-label="Error risk by selected blocks">
            {errorHeatline.map((row) => (
              <div key={row.block_name} className="dbx-risk-row">
                <b>{row.label}</b>
                <div className="dbx-risk-track">
                  <span
                    className={`dbx-risk-point ${row.riskClass}`}
                    style={{ left: `${row.position}%` }}
                  >
                    {row.displayPct}
                  </span>
                </div>
              </div>
            ))}
            <div className="dbx-matrix-scale">
              <span>0% Low</span>
              <span>15% Watch</span>
              <span>25%+ Critical</span>
            </div>
          </div>
        </article>
      </section>

      <div className="dbx-section-title" id="dbx-ranking">
        <div>
          <h2>Block-Wise Performance Ranking</h2>
          <p>
            The full table remains available, but it is cleaner, sorted, and uses status badges for
            faster scanning of volume, dropout and data-quality concerns.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-block-actions")}>
          Recommended actions →
        </button>
      </div>

      <section className="dbx-panel dbx-table-card">
        <div className="dbx-table-head">
          <div>
            <h3>Block Performance Table</h3>
            <p>Sorted by total remarks. Error status: Green ≤ 5%, Amber 5–15%, Red &gt; 15%.</p>
          </div>
          <span className="dbx-pill">{rankingTable.length} blocks</span>
        </div>
        <div className="dbx-block-table-scroll">
          <table className="dbx-overview-table dbx-block-table" aria-label="Block-wise performance ranking table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th className="num">Schools</th>
                <th className="num">Total Remarks</th>
                <th className="num">Avg/School</th>
                <th className="num">Dropout</th>
                <th className="num">Class 12 Passed</th>
                <th className="num">Error %</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rankingTable.map((row) => (
                <tr key={row.block_name}>
                  <td>{row.rank}</td>
                  <td>
                    <BlockLink blockCode={row.block_code} className="dbx-block-link">
                      {row.block_name}
                    </BlockLink>
                  </td>
                  <td className="num">{fmt(row.total_schools)}</td>
                  <td className="num">{fmt(row.total_remarks)}</td>
                  <td className="num">{row.avg_remarks_per_school}</td>
                  <td className="num">{fmt(row.dropout)}</td>
                  <td className="num">{fmt(row.class12_passed)}</td>
                  <td className="num">{pct(row.error_pct)}</td>
                  <td>
                    <span className={`dbx-badge ${row.status.badgeClass}`}>{row.status.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="dbx-table-foot">
          <span>
            Showing {rankingTable.length} of {rankingTable.length} blocks in current district scope
          </span>
          <span>Total remarks shown: {fmt(tableRemarksTotal)}</span>
        </div>
      </section>

      <div className="dbx-section-title">
        <div>
          <h2>Category Mix and Action Queue</h2>
          <p>
            The stacked mix is transformed into compact horizontal bars, followed by a focused queue
            for district review owners.
          </p>
        </div>
        <button type="button" className="dbx-section-action" onClick={() => scrollToId("dbx-block-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="dbx-block-two-column" id="dbx-block-actions">
        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Block-Wise Category Mix</h3>
              <p>Major remark categories by selected high-volume blocks.</p>
            </div>
            <span className="dbx-pill">Top 8</span>
          </div>
          <div className="dbx-mix-list">
            {categoryMix.map((row) => (
              <div key={row.block_name} className="dbx-mix-row">
                <b>{row.label}</b>
                <div className="dbx-stack">
                  {row.segments
                    .filter((seg) => seg.widthPct > 0)
                    .map((seg) => (
                      <i
                        key={seg.key}
                        className={seg.className.replace("seg-", "dbx-seg-")}
                        style={{ width: `${seg.widthPct}%` }}
                      />
                    ))}
                </div>
                <span>{row.displayTotal}</span>
              </div>
            ))}
          </div>
          <div className="dbx-mix-legend">
            <span>
              <i className="dbx-seg-green" />
              Class 12 Passed
            </span>
            <span>
              <i className="dbx-seg-red" />
              Dropout
            </span>
            <span>
              <i className="dbx-seg-purple" />
              ITI/Poly
            </span>
            <span>
              <i className="dbx-seg-blue" />
              Migration
            </span>
          </div>
        </article>

        <article className="dbx-panel">
          <div className="dbx-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Actions are grouped by what should happen next, not just by what the charts show.</p>
            </div>
            <span className="dbx-pill amber">Priority</span>
          </div>
          <div className="dbx-block-action-card">
            {actions.items.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? LineChart;
              return (
                <div key={item.title} className="dbx-block-action-item">
                  <div className={`dbx-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="dbx-action-footer">
              <b>Next review recommendation</b>
              <span>{actions.footer}</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default DropboxBlockPerformanceTab;
